import * as qs from 'qs';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import type { VoidFunctionComponent } from 'react';

import { ENDPOINTS } from '../../../shared/organizationPackages/helpers';
import { useAddToast } from '../../contexts/ToastContext';
import { track } from '../../helpers/analytics';
import { get } from '../../redux/api';
import * as PackageInventoryStore from '../../redux/packageInventory';
import Button from '../generic/Button';
import { useAddJobToNotificationQueue } from '../JobNotificationHub/Provider';
import type { AnyFilter } from '../../../shared/organizationPackages/filters';

/**
 * A button that allows the user to export the package index.
 *
 * Clicking the button calls the appropriate route and triggers the generation of the package index report.
 */
export const PackageIndexExportButton: VoidFunctionComponent = () => {
  const addToast = useAddToast();
  const rawFilters: AnyFilter = useSelector(PackageInventoryStore.selectors.getFilters);
  const queryParameters = useMemo(() => qs.stringify(rawFilters), [rawFilters]);
  const addJob = useAddJobToNotificationQueue();

  const exportAndShowToast = async () => {
    track('Package Index Export triggered', { filters: JSON.stringify(rawFilters, null, 2) });
    const { data } = await get<{
      task: {
        jobToken: string;
        task: string;
      };
      target: string;
    }>(`${ENDPOINTS.PACKAGE_INDEX_REPORT}?${queryParameters}`);
    if (!data) {
      addToast({
        type: 'error',
        content: 'Failed to queue package index export.',
      });
      throw new Error('Failed to queue package index export.');
    }
    addJob(data.task.jobToken, data.task.task);
    addToast({ type: 'success', content: 'Package Index export queued.  It will be emailed to you once completed.' });
  };

  const handleClick = () => {
    exportAndShowToast().catch(() => {
      // Error already handled by showing toast, just prevent unhandled rejection
    });
  };

  return <Button onClick={handleClick}>Export</Button>;
};

export default PackageIndexExportButton;
